'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'
import { AuthCaptchaError, type AuthCaptchaAction, type CaptchaTokenProvider } from '@/lib/authCaptcha'
import { isDesktopApp } from '@/lib/desktopApp'
import { captureSentryError } from '@/lib/sentry'

interface Challenge {
  id: number
  succeed: (token: string) => void
  fail: (reason: AuthCaptchaError['reason'], code?: string) => void
}

/** Each request mounts a fresh widget: tokens never survive an Auth attempt. */
export function useAuthCaptcha(action: AuthCaptchaAction, enabled = true) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim()
  const disabled = process.env.NEXT_PUBLIC_AUTH_CAPTCHA_DISABLED === 'true'
  const widgetId = useId()
  const sequence = useRef(0)
  const pending = useRef<Challenge | null>(null)
  const mounted = useRef(enabled)
  const [challenge, setChallenge] = useState<Challenge | null>(null)

  useEffect(() => {
    mounted.current = enabled
    return () => {
      pending.current?.fail('cancelled')
      mounted.current = false
    }
  }, [action, enabled])

  const getCaptchaToken: CaptchaTokenProvider = useCallback(() => {
    if (!mounted.current || !enabled) return Promise.reject(new AuthCaptchaError('cancelled'))
    // Disabling the client challenge is an explicit deployment choice and must
    // match the Auth server. A missing key is never an implicit opt-out.
    if (disabled) return Promise.resolve(undefined)
    // The desktop shell (Electron, see desktop/src/main.ts) cannot complete
    // Turnstile's challenge: it fails or hangs, and the app never gets past the
    // connection-error screen. It signs in without a token, which only works
    // while hosted Auth CAPTCHA stays disabled (docs/auth-captcha-rollout.md).
    if (isDesktopApp()) return Promise.resolve(undefined)
    if (!siteKey) {
      const error = new AuthCaptchaError('missing_site_key')
      captureSentryError(error, { operation: 'auth_captcha', tags: { action, reason: error.reason } })
      return Promise.reject(error)
    }

    pending.current?.fail('cancelled')
    return new Promise<string>((resolve, reject) => {
      const finish = () => {
        if (pending.current !== request) return false
        clearTimeout(timeout)
        pending.current = null
        if (mounted.current) setChallenge(null)
        return true
      }
      const request: Challenge = {
        id: ++sequence.current,
        succeed: token => {
          if (!token) return request.fail('failed')
          if (finish()) resolve(token)
        },
        fail: (reason, code) => {
          if (!finish()) return
          const error = new AuthCaptchaError(reason, code)
          if (reason !== 'cancelled') {
            captureSentryError(error, {
              operation: 'auth_captcha',
              tags: code ? { action, reason, code } : { action, reason },
            })
          }
          reject(error)
        },
      }
      // Also bounds a blocked script that never invokes a widget callback.
      const timeout = setTimeout(() => request.fail('timeout'), 90_000)
      pending.current = request
      setChallenge(request)
    })
  }, [action, disabled, enabled, siteKey])

  const captcha =
    enabled && challenge && siteKey ? (
      <Turnstile
        key={challenge.id}
        id={`${widgetId}-${challenge.id}`}
        siteKey={siteKey}
        onSuccess={challenge.succeed}
        onError={code => challenge.fail('failed', code || 'widget_error')}
        onExpire={() => challenge.fail('expired')}
        onTimeout={() => challenge.fail('timeout')}
        onUnsupported={() => challenge.fail('failed', 'unsupported_browser')}
        scriptOptions={{ onError: () => challenge.fail('failed', 'script_load') }}
        options={{
          action,
          size: 'flexible',
          theme: 'auto',
          appearance: 'interaction-only',
          retry: 'never',
          refreshExpired: 'never',
          refreshTimeout: 'never',
        }}
      />
    ) : null

  return { getCaptchaToken, captcha }
}
